/**
 * Infrastructure Seed Data Module
 * Data from Brett's spreadsheet: 9. IT Infrastructure
 *
 * Sprint 36: Infrastructure data for Full Topology View
 */

export interface InfrastructureItem {
    ciId: string;
    name: string;
    type: string;
    environment: string;
    location: string;
    supportsApplications: string[];
    specification: string;
    owner: string;
    criticality: 'Critical' | 'High' | 'Medium' | 'Low';
}

export interface ApplicationItem {
    appId: string;
    name: string;
    vendor: string;
    type: string;
    domain: string;
    description: string;
    criticality: 'Critical' | 'High' | 'Medium' | 'Low';
}

/**
 * IT Infrastructure data from spreadsheet
 */
export const INFRASTRUCTURE_DATA: InfrastructureItem[] = [
    // AWS Cloud Infrastructure
    { ciId: 'CI-AWS-001', name: 'AWS Production VPC', type: 'Cloud VPC', environment: 'Production', location: 'ap-southeast-2', supportsApplications: ['Digital Platform', 'API Gateway'], specification: 'Multi-AZ, 3 subnets', owner: 'Cloud Team', criticality: 'Critical' },
    { ciId: 'CI-AWS-002', name: 'AWS Card Cluster', type: 'EKS Cluster', environment: 'Production', location: 'ap-southeast-2', supportsApplications: ['Card APIs', 'Fraud Detection'], specification: '20 nodes, m5.2xlarge', owner: 'Cloud Team', criticality: 'Critical' },
    { ciId: 'CI-AWS-003', name: 'AWS Loan Cluster', type: 'EKS Cluster', environment: 'Production', location: 'ap-southeast-2', supportsApplications: ['Loan APIs', 'Credit Engine'], specification: '15 nodes, m5.2xlarge', owner: 'Cloud Team', criticality: 'Critical' },
    { ciId: 'CI-AWS-004', name: 'AWS Data Lake', type: 'S3 + Glue', environment: 'Production', location: 'ap-southeast-2', supportsApplications: ['Data Warehouse', 'Analytics'], specification: '500TB storage', owner: 'Data Team', criticality: 'High' },
    { ciId: 'CI-AWS-005', name: 'AWS DR Region', type: 'Cloud VPC', environment: 'DR', location: 'ap-southeast-1', supportsApplications: ['All critical systems'], specification: 'Pilot light DR', owner: 'Cloud Team', criticality: 'Critical' },

    // Data Centre 1 - Production
    { ciId: 'CI-DC1-001', name: 'Core Banking Server 1', type: 'Physical Server', environment: 'Production', location: 'Data Centre 1', supportsApplications: ['Core Banking System'], specification: '4x Intel Xeon, 512GB RAM', owner: 'Infrastructure Team', criticality: 'Critical' },
    { ciId: 'CI-DC1-002', name: 'Core Banking Server 2', type: 'Physical Server', environment: 'Production', location: 'Data Centre 1', supportsApplications: ['Core Banking System'], specification: '4x Intel Xeon, 512GB RAM', owner: 'Infrastructure Team', criticality: 'Critical' },
    { ciId: 'CI-DC1-003', name: 'Card Management Server 1', type: 'Physical Server', environment: 'Production', location: 'Data Centre 1', supportsApplications: ['Card Management System'], specification: '2x Intel Xeon, 256GB RAM', owner: 'Infrastructure Team', criticality: 'Critical' },
    { ciId: 'CI-DC1-004', name: 'Card Management Server 2', type: 'Physical Server', environment: 'Production', location: 'Data Centre 1', supportsApplications: ['Card Management System'], specification: '2x Intel Xeon, 256GB RAM', owner: 'Infrastructure Team', criticality: 'Critical' },
    { ciId: 'CI-DC1-005', name: 'Loan Origination Server', type: 'Physical Server', environment: 'Production', location: 'Data Centre 1', supportsApplications: ['Loan Origination System'], specification: '2x Intel Xeon, 256GB RAM', owner: 'Infrastructure Team', criticality: 'Critical' },
    { ciId: 'CI-DC1-006', name: 'HSM Cluster', type: 'HSM', environment: 'Production', location: 'Data Centre 1', supportsApplications: ['PIN Management', 'Crypto'], specification: 'Thales Luna Network HSM', owner: 'Security Team', criticality: 'Critical' },
    { ciId: 'CI-DC1-007', name: 'Oracle RAC Cluster', type: 'Database Cluster', environment: 'Production', location: 'Data Centre 1', supportsApplications: ['Core Banking', 'Cards', 'Loans DB'], specification: '4 node RAC, 2TB each', owner: 'DBA Team', criticality: 'Critical' },
    { ciId: 'CI-DC1-008', name: 'Payment Switch', type: 'Network Appliance', environment: 'Production', location: 'Data Centre 1', supportsApplications: ['Card Transaction Switching'], specification: 'ACI BASE24', owner: 'Payments Team', criticality: 'Critical' },

    // Data Centre 2 - DR
    { ciId: 'CI-DC2-001', name: 'Core Banking DR Server', type: 'Physical Server', environment: 'DR', location: 'Data Centre 2', supportsApplications: ['Core Banking System'], specification: '4x Intel Xeon, 512GB RAM', owner: 'Infrastructure Team', criticality: 'Critical' },
    { ciId: 'CI-DC2-002', name: 'Card Management DR Server', type: 'Physical Server', environment: 'DR', location: 'Data Centre 2', supportsApplications: ['Card Management System'], specification: '2x Intel Xeon, 256GB RAM', owner: 'Infrastructure Team', criticality: 'Critical' },
    { ciId: 'CI-DC2-003', name: 'Oracle DR Cluster', type: 'Database Cluster', environment: 'DR', location: 'Data Centre 2', supportsApplications: ['All Databases'], specification: '2 node Data Guard', owner: 'DBA Team', criticality: 'Critical' },

    // Network Infrastructure
    { ciId: 'CI-NET-001', name: 'Core Network Switch DC1', type: 'Network Switch', environment: 'Production', location: 'Data Centre 1', supportsApplications: ['All DC1 systems'], specification: 'Cisco Nexus 9000', owner: 'Network Team', criticality: 'Critical' },
    { ciId: 'CI-NET-002', name: 'Core Network Switch DC2', type: 'Network Switch', environment: 'DR', location: 'Data Centre 2', supportsApplications: ['All DC2 systems'], specification: 'Cisco Nexus 9000', owner: 'Network Team', criticality: 'Critical' },
    { ciId: 'CI-NET-003', name: 'Internet Gateway', type: 'Firewall/Router', environment: 'Production', location: 'Data Centre 1', supportsApplications: ['External connectivity'], specification: 'Palo Alto', owner: 'Network Team', criticality: 'Critical' },
    { ciId: 'CI-NET-004', name: 'MPLS Router DC1', type: 'Router', environment: 'Production', location: 'Data Centre 1', supportsApplications: ['Branch connectivity'], specification: 'Cisco ASR', owner: 'Network Team', criticality: 'Critical' },
    { ciId: 'CI-NET-005', name: 'Load Balancer DC1', type: 'Load Balancer', environment: 'Production', location: 'Data Centre 1', supportsApplications: ['All web services'], specification: 'F5 BIG-IP', owner: 'Network Team', criticality: 'Critical' },

    // Integration Infrastructure
    { ciId: 'CI-INT-001', name: 'API Gateway Cluster', type: 'API Gateway', environment: 'Production', location: 'AWS', supportsApplications: ['All external APIs'], specification: 'Kong Enterprise', owner: 'Integration Team', criticality: 'Critical' },
    { ciId: 'CI-INT-002', name: 'MQ Broker Cluster', type: 'Message Broker', environment: 'Production', location: 'Data Centre 1', supportsApplications: ['Async messaging'], specification: 'IBM MQ, 3 node', owner: 'Integration Team', criticality: 'Critical' },
    { ciId: 'CI-INT-003', name: 'Kafka Cluster', type: 'Event Streaming', environment: 'Production', location: 'AWS', supportsApplications: ['Real-time events'], specification: 'Confluent, 6 broker', owner: 'Integration Team', criticality: 'High' },
    { ciId: 'CI-INT-004', name: 'ESB Cluster', type: 'Integration', environment: 'Production', location: 'Data Centre 1', supportsApplications: ['Legacy integration'], specification: 'MuleSoft', owner: 'Integration Team', criticality: 'High' },
];

/**
 * Application data from spreadsheet
 */
export const APPLICATION_DATA: ApplicationItem[] = [
    // Core Systems
    { appId: 'APP-001', name: 'Core Banking System', vendor: 'Temenos', type: 'Core', domain: 'Banking', description: 'Core deposit and transaction account system', criticality: 'Critical' },
    { appId: 'APP-002', name: 'Card Management System', vendor: 'FIS', type: 'Core', domain: 'Cards', description: 'Credit and debit card account management', criticality: 'Critical' },
    { appId: 'APP-003', name: 'Loan Origination System', vendor: 'Finastra', type: 'Core', domain: 'Lending', description: 'Loan application and origination', criticality: 'Critical' },
    { appId: 'APP-004', name: 'Loan Servicing System', vendor: 'Finastra', type: 'Core', domain: 'Lending', description: 'Loan account management and servicing', criticality: 'Critical' },

    // CRM & Channels
    { appId: 'APP-010', name: 'CRM (Salesforce)', vendor: 'Salesforce', type: 'CRM', domain: 'Customer', description: 'Customer relationship management, 360 view', criticality: 'High' },
    { appId: 'APP-011', name: 'Digital Banking Platform', vendor: 'Backbase', type: 'Channel', domain: 'Digital', description: 'Internet and mobile banking', criticality: 'Critical' },
    { appId: 'APP-012', name: 'Contact Centre Platform', vendor: 'Genesys', type: 'Channel', domain: 'Service', description: 'Voice and digital contact centre', criticality: 'High' },
    { appId: 'APP-013', name: 'Branch Platform', vendor: 'Custom', type: 'Channel', domain: 'Sales', description: 'Branch teller and sales system', criticality: 'High' },

    // Card Operations
    { appId: 'APP-020', name: 'Card Personalization', vendor: 'IDEMIA', type: 'Operations', domain: 'Cards', description: 'Physical card production and personalization', criticality: 'High' },
    { appId: 'APP-021', name: 'PIN Management', vendor: 'Thales', type: 'Security', domain: 'Cards', description: 'PIN generation, storage, verification', criticality: 'Critical' },
    { appId: 'APP-022', name: 'Card Fraud Platform', vendor: 'FICO', type: 'Risk', domain: 'Cards', description: 'Real-time fraud detection and case management', criticality: 'Critical' },
    { appId: 'APP-023', name: 'Dispute Management', vendor: 'FIS', type: 'Operations', domain: 'Cards', description: 'Card dispute and chargeback processing', criticality: 'High' },
    { appId: 'APP-024', name: 'Loyalty Platform', vendor: 'Collinson', type: 'Product', domain: 'Cards', description: 'Points accrual, redemption, partner management', criticality: 'Medium' },
    { appId: 'APP-025', name: 'Card Billing System', vendor: 'FIS', type: 'Operations', domain: 'Cards', description: 'Statement generation and billing', criticality: 'High' },

    // Lending
    { appId: 'APP-030', name: 'Credit Decision Engine', vendor: 'Experian', type: 'Risk', domain: 'Lending', description: 'Automated credit decisioning', criticality: 'Critical' },
    { appId: 'APP-031', name: 'Underwriting Workbench', vendor: 'Custom', type: 'Operations', domain: 'Lending', description: 'Manual underwriting interface', criticality: 'High' },
    { appId: 'APP-032', name: 'Valuation Platform', vendor: 'CoreLogic', type: 'Operations', domain: 'Lending', description: 'Property valuation ordering and management', criticality: 'High' },
    { appId: 'APP-033', name: 'Settlement System', vendor: 'Custom', type: 'Operations', domain: 'Lending', description: 'Loan settlement and funding', criticality: 'Critical' },
    { appId: 'APP-034', name: 'PEXA', vendor: 'PEXA', type: 'External', domain: 'Lending', description: 'Electronic property settlement', criticality: 'Critical' },

    // Operations & Workflow
    { appId: 'APP-040', name: 'ServiceNow', vendor: 'ServiceNow', type: 'Workflow', domain: 'Operations', description: 'Case management, workflow, ITSM', criticality: 'High' },
    { appId: 'APP-041', name: 'Document Management', vendor: 'OpenText', type: 'Content', domain: 'Operations', description: 'Document capture, storage, retrieval', criticality: 'High' },
    { appId: 'APP-042', name: 'Collections Platform', vendor: 'FICO', type: 'Operations', domain: 'Collections', description: 'Collections workflow and dialler', criticality: 'High' },

    // Risk & Compliance
    { appId: 'APP-050', name: 'AML Platform', vendor: 'NICE Actimize', type: 'Compliance', domain: 'Risk', description: 'Transaction monitoring, SAR management', criticality: 'Critical' },
    { appId: 'APP-051', name: 'KYC Platform', vendor: 'Refinitiv', type: 'Compliance', domain: 'Risk', description: 'Customer due diligence, screening', criticality: 'High' },
    { appId: 'APP-052', name: 'Credit Bureau', vendor: 'Equifax/Experian', type: 'External', domain: 'Risk', description: 'Credit file access', criticality: 'Critical' },

    // Payments
    { appId: 'APP-060', name: 'Payment Hub', vendor: 'Volante', type: 'Core', domain: 'Payments', description: 'Payment processing and routing', criticality: 'Critical' },
    { appId: 'APP-061', name: 'NPP Gateway', vendor: 'Custom', type: 'Channel', domain: 'Payments', description: 'New Payments Platform connectivity', criticality: 'Critical' },

    // Analytics
    { appId: 'APP-070', name: 'Data Warehouse', vendor: 'Snowflake', type: 'Data', domain: 'Analytics', description: 'Enterprise data warehouse', criticality: 'High' },
    { appId: 'APP-071', name: 'BI Platform', vendor: 'Tableau', type: 'Analytics', domain: 'Analytics', description: 'Reporting and visualization', criticality: 'Medium' },
];

/**
 * Group infrastructure by location for visualization
 */
export function groupInfrastructureByLocation(): Map<string, InfrastructureItem[]> {
    const grouped = new Map<string, InfrastructureItem[]>();

    for (const item of INFRASTRUCTURE_DATA) {
        const location = item.location;
        if (!grouped.has(location)) {
            grouped.set(location, []);
        }
        grouped.get(location)!.push(item);
    }

    return grouped;
}

/**
 * Group infrastructure by type for visualization
 */
export function groupInfrastructureByType(): Map<string, InfrastructureItem[]> {
    const grouped = new Map<string, InfrastructureItem[]>();

    for (const item of INFRASTRUCTURE_DATA) {
        const type = item.type;
        if (!grouped.has(type)) {
            grouped.set(type, []);
        }
        grouped.get(type)!.push(item);
    }

    return grouped;
}

/**
 * Get infrastructure color by type
 */
export function getInfrastructureColor(type: string): string {
    const colors: Record<string, string> = {
        'Cloud VPC': '#0ea5e9',        // Sky blue
        'EKS Cluster': '#06b6d4',      // Cyan
        'S3 + Glue': '#8b5cf6',        // Purple
        'Physical Server': '#64748b',   // Slate
        'Database Cluster': '#f59e0b',  // Amber
        'HSM': '#ef4444',              // Red (security)
        'Network Appliance': '#22c55e', // Green
        'Network Switch': '#10b981',    // Emerald
        'Firewall/Router': '#f97316',   // Orange
        'Router': '#84cc16',            // Lime
        'Load Balancer': '#14b8a6',     // Teal
        'API Gateway': '#6366f1',       // Indigo
        'Message Broker': '#ec4899',    // Pink
        'Event Streaming': '#a855f7',   // Violet
        'Integration': '#f472b6',       // Light pink
    };

    return colors[type] || '#64748b';
}

/**
 * Get application color by domain
 */
export function getApplicationColor(domain: string): string {
    const colors: Record<string, string> = {
        'Banking': '#1e40af',      // Deep blue
        'Cards': '#dc2626',        // Red
        'Lending': '#16a34a',      // Green
        'Customer': '#7c3aed',     // Purple
        'Digital': '#0ea5e9',      // Sky blue
        'Service': '#f59e0b',      // Amber
        'Sales': '#ec4899',        // Pink
        'Operations': '#64748b',   // Slate
        'Risk': '#ef4444',         // Red
        'Collections': '#f97316',  // Orange
        'Payments': '#06b6d4',     // Cyan
        'Analytics': '#8b5cf6',    // Purple
    };

    return colors[domain] || '#64748b';
}
